import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

interface ColorScheme {
    primary: string;
    secondary: string;
    accent: string;
    navigation: string;
    banner: string;
    logo: string;
}

type ColorSchemeName = 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'teal' | 'indigo' | 'pink';

// Preset color schemes
const PRESET_COLORS: Record<ColorSchemeName, ColorScheme> = {
    blue: {
        primary: '#007bff',
        secondary: '#6c757d',
        accent: '#28a745',
        navigation: '#0056b3',
        banner: '#e9ecef',
        logo: '#0056b3'
    },
    purple: {
        primary: '#6f42c1',
        secondary: '#5a6268',
        accent: '#20c997',
        navigation: '#563d7c',
        banner: '#f8f9fa',
        logo: '#563d7c'
    },
    green: {
        primary: '#28a745',
        secondary: '#5a6268',
        accent: '#17a2b8',
        navigation: '#1e7e34',
        banner: '#f8f9fa',
        logo: '#1e7e34'
    },
    red: {
        primary: '#dc3545',
        secondary: '#5a6268',
        accent: '#ffc107',
        navigation: '#bd2130',
        banner: '#f8f9fa',
        logo: '#bd2130'
    },
    orange: {
        primary: '#fd7e14',
        secondary: '#5a6268',
        accent: '#17a2b8',
        navigation: '#d65d0b',
        banner: '#f8f9fa',
        logo: '#d65d0b'
    },
    teal: {
        primary: '#20c997',
        secondary: '#5a6268',
        accent: '#6f42c1',
        navigation: '#1a9f7d',
        banner: '#f8f9fa',
        logo: '#1a9f7d'
    },
    indigo: {
        primary: '#6610f2',
        secondary: '#5a6268',
        accent: '#fd7e14',
        navigation: '#520dc2',
        banner: '#f8f9fa',
        logo: '#520dc2'
    },
    pink: {
        primary: '#e83e8c',
        secondary: '#5a6268',
        accent: '#20c997',
        navigation: '#d91a72',
        banner: '#f8f9fa',
        logo: '#d91a72'
    }
};

interface ThemeContextValue {
    theme: string;
    colorScheme: ColorSchemeName;
    colors: ColorScheme;
    presetColors: Record<ColorSchemeName, ColorScheme>;
    toggleTheme: () => void;
    changeColorScheme: (scheme: ColorSchemeName) => void;
    updateCustomColors: (newColors: Partial<ColorScheme>) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setTheme] = useState<string>(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme || 'light';
    });

    const [colorScheme, setColorScheme] = useState<ColorSchemeName>(() => {
        const savedScheme = localStorage.getItem('colorScheme') as ColorSchemeName;
        return savedScheme || 'blue';
    });

    const [customColors, setCustomColors] = useState<ColorScheme>(() => {
        const savedColors = localStorage.getItem('customColors');
        return savedColors ? JSON.parse(savedColors) : PRESET_COLORS.blue;
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        const colors = PRESET_COLORS[colorScheme] || customColors;
        updateThemeColors(colors);
        localStorage.setItem('colorScheme', colorScheme);
    }, [colorScheme]);

    useEffect(() => {
        localStorage.setItem('customColors', JSON.stringify(customColors));
        updateThemeColors(customColors);
    }, [customColors]);

    const updateThemeColors = (colors: ColorScheme) => {
        Object.entries(colors).forEach(([key, value]) => {
            document.documentElement.style.setProperty(`--${key}-color`, value);
        });
    };

    const toggleTheme = () => {
        setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
    };

    const changeColorScheme = (scheme: ColorSchemeName) => {
        setColorScheme(scheme);
    };

    const updateCustomColors = (newColors: Partial<ColorScheme>) => {
        setCustomColors(prevColors => ({
            ...prevColors,
            ...newColors
        }));
    };

    const value: ThemeContextValue = {
        theme, 
        colorScheme,
        colors: PRESET_COLORS[colorScheme] || customColors,
        presetColors: PRESET_COLORS,
        toggleTheme, 
        changeColorScheme,
        updateCustomColors
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

